import { contractRequest } from "./transport";
import type { RecordReview, ReviewTriggerType } from "@/features/patient/records/api/dataPipelineApi";
import type { DecisionAction, PatientResponse } from "@/portals/patient/api/reviewItemsApi";

export type { DecisionAction };

// Module 4 — physician review queue. Physician token on every call; failures go
// through the one contract error parser (contractErrors.ts).

export type PhysicianItemStatus = "AWAITING_PHYSICIAN" | "IN_PHYSICIAN_REVIEW" | "NEEDS_MORE_INFO" | "RESOLVED_PATIENT_REPORTED" | "RESOLVED_PHYSICIAN_VERIFIED";
export type AttachedVia = "CLAIMED" | "ASSIGNED_BY_ADMIN";

/** Shared-queue row. Deliberately carries no patient identity. */
export interface QueueItem {
  id: string;
  triggerType: ReviewTriggerType;
  medicineName: string;
  recordCount: number;
  createdAt: string;
}

export interface MyReviewItem {
  id: string;
  triggerType: ReviewTriggerType;
  itemStatus: PhysicianItemStatus;
  medicineName: string;
  patientDisplayName: string;
  attachedVia: AttachedVia;
  attachedAt: string;
}

export interface RxNormMatch {
  rxcui: string | null;
  matchLevel: string | null;
  confidenceScore: number | null;
  confidenceBand: string | null;
  matchSource: string | null;
}

export interface PhysicianReviewRecord {
  id: string;
  recordRole: RecordReview["recordRole"];
  sourceVendor: string;
  createdAt: string;
  fields: Record<string, unknown>;
  rxnorm: RxNormMatch | null;
}

/** Keys a CORRECT_VALUE decision may change — no others. */
export const CORRECTABLE_FIELDS = ["medication_name", "dose_quantity", "dose_unit", "frequency", "route", "dosage_text", "status"] as const;
export type CorrectableField = (typeof CORRECTABLE_FIELDS)[number];
export type CorrectedValue = Partial<Record<CorrectableField, string | number>>;

/** A recorded correction: the values sent, plus the backend's RxNorm lookup of the
 *  corrected medicine (null when absent or the lookup failed). */
export type RecordedCorrection = CorrectedValue & {
  rxnorm?: Omit<RxNormMatch, "matchSource"> | null;
};

export interface PhysicianAction {
  action: DecisionAction;
  reason: string;
  targetRecordId: string | null;
  correctedValue: RecordedCorrection | null;
  createdAt: string;
}

export interface DecisionRequest {
  action: DecisionAction;
  reason: string;
  targetRecordId?: string;
  correctedValue?: CorrectedValue;
}

export const REASON_MAX_LENGTH = 2000;

export interface PhysicianReviewItem {
  id: string;
  triggerType: ReviewTriggerType;
  itemStatus: PhysicianItemStatus;
  createdAt: string;
  attachedVia: AttachedVia;
  attachedAt: string;
  patient: { displayName: string; dateOfBirth: string };
  records: PhysicianReviewRecord[];
  /** Oldest first. */
  patientResponses: PatientResponse[];
  /** Module 5 — oldest first. Optional for older backends. */
  physicianActions?: PhysicianAction[];
}

/** Oldest first. Only AWAITING_PHYSICIAN items nobody holds. */
export const listReviewQueueApi = () =>
  contractRequest<{ items: QueueItem[] }>("/api/physician/review-queue", { method: "GET", portal: "physician" });

/** 200 item · 409 ITEM_ALREADY_CLAIMED · 404 REVIEW_ITEM_NOT_FOUND */
export const claimReviewItemApi = (id: string) =>
  contractRequest<PhysicianReviewItem>(`/api/physician/review-queue/${encodeURIComponent(id)}/claim`, { method: "POST", portal: "physician" });

/** "active" (default) = IN_PHYSICIAN_REVIEW or NEEDS_MORE_INFO held by me. */
export const listMyReviewItemsApi = (state: "active" | "all" = "active") =>
  contractRequest<{ items: MyReviewItem[] }>(`/api/physician/review-items?state=${state}`, { method: "GET", portal: "physician" });

/** 404 REVIEW_ITEM_NOT_FOUND when not held by me. */
export const getPhysicianReviewItemApi = (id: string) =>
  contractRequest<PhysicianReviewItem>(`/api/physician/review-items/${encodeURIComponent(id)}`, { method: "GET", portal: "physician" });

/** 200 · 409 ITEM_NOT_RELEASABLE · 404 REVIEW_ITEM_NOT_FOUND */
export const releaseReviewItemApi = (id: string) =>
  contractRequest<{ id: string; itemStatus: "AWAITING_PHYSICIAN" }>(`/api/physician/review-items/${encodeURIComponent(id)}/release`, { method: "POST", portal: "physician" });

/** 200 updated item · 404 REVIEW_ITEM_NOT_FOUND · 409 ITEM_NOT_DECIDABLE · 400 VALIDATION_ERROR */
export const decideReviewItemApi = (id: string, body: DecisionRequest) =>
  contractRequest<PhysicianReviewItem>(`/api/physician/review-items/${encodeURIComponent(id)}/decisions`, {
    method: "POST",
    portal: "physician",
    body,
  });
